// Generated by Copilot- Start - 2026-03-18 16:30:25
/**
 * ============================================================================
 * SVODS.TS - SVOD tiles, packages, and analytics
 * ============================================================================
 *
 * This file consolidates all SVOD-related operations:
 * - SVOD section building and package lists
 * - Analytics attachment
 *
 * Replaces: svod-section.ts, svod-packages.ts, analytics.ts
 */

import { PackageSummary } from "./types";
import { ComponentNames } from "../../constant/component-constant";
import ReportingService from "../../../src/services/ReportingService";
import { logAdapterEvent } from "../logger";

const HELPER_NAME = "channel-interactive-tool-v2/svods";

// ============================================================================
// SECTION 1: SVOD PACKAGE LISTS
// ============================================================================

export interface SvodPackageItem {
  id: string;
  displayLabel: string;
  packageLegal: string;
  ctaObj: any;
  type: string;
}

export interface BuildSvodPackageListsArgs {
  packageListSystem?: string[];
  genrePacks?: string[];
  packageSummaries: Record<string, PackageSummary>;
  chnlSrc: string;
  genreConfig?: Record<string, boolean>;
}

/**
 * Builds signaturePackageList and genrePackageList for a single SVOD tile.
 *
 * - signaturePackageList: packages from packageListSystem that match the
 *   allowed source type (internet for stream, satellite for dtv)
 * - genrePackageList: packages from genrePacks that are genre/genre-mini type
 *   and not explicitly disabled in genreConfig
 *
 * Used by the V2 adapter to produce the shape expected by SvodBannerV2/SvodModalV2.
 */
export const buildSvodPackageLists = (args: BuildSvodPackageListsArgs): {
  signaturePackageList: SvodPackageItem[];
  genrePackageList: SvodPackageItem[];
} => {
  const { packageListSystem, genrePacks, packageSummaries, chnlSrc, genreConfig } = args;

  const signaturePackageList: SvodPackageItem[] = [];
  const genrePackageList: SvodPackageItem[] = [];

  // Signature packages: match allowed types by chnlSrc
  const allowedSignatureTypes = chnlSrc === "streamclup" ?
    ["internet"] :
    chnlSrc === "dtvclup" ?
      ["satellite"] :
      ["internet", "satellite"];

  const allowedGenreTypes = ["genre", "genre-mini"];

  if (Array.isArray(packageListSystem)) {
    packageListSystem.forEach((pkgId: string) => {
      if (!pkgId) {
        return;
      }
      const summary = packageSummaries[pkgId];
      if (!summary) {
        return;
      }
      const pkgType = summary.type;
      if (!pkgType || !allowedSignatureTypes.includes(pkgType)) {
        return;
      }
      signaturePackageList.push({
        id: pkgId,
        displayLabel: summary.displayLabel || pkgId,
        packageLegal: "Included in package",
        ctaObj: summary.ctaObj || null,
        type: pkgType
      });
    });
  }

  if (Array.isArray(genrePacks)) {
    genrePacks.forEach((pkgId: string) => {
      if (!pkgId) {
        return;
      }
      const summary = packageSummaries[pkgId];
      if (!summary) {
        return;
      }
      const pkgType = summary.type;
      if (!pkgType || !allowedGenreTypes.includes(pkgType)) {
        return;
      }
      const isEnabled = genreConfig ? genreConfig[pkgId] !== false : true;
      if (!isEnabled) {
        return;
      }
      genrePackageList.push({
        id: pkgId,
        displayLabel: summary.displayLabel || pkgId,
        packageLegal: "Included in package",
        ctaObj: summary.ctaObj || null,
        type: pkgType
      });
    });
  }

  logAdapterEvent(HELPER_NAME, "built SVOD package lists", {
    signatureCount: signaturePackageList.length,
    genreCount: genrePackageList.length,
    chnlSrc
  });

  return {
    signaturePackageList,
    genrePackageList
  };
};
// Generated by Copilot- End - 2026-03-18 16:30:25

// ============================================================================
// SECTION 2: ANALYTICS
// ============================================================================

/**
 * Attaches analytics data to component props
 * Wraps ReportingService.fixAnalyticsValues for consistent analytics structure
 *
 * @param params - Object containing content and index
 * @returns Analytics object ready for component props
 */
export const attachAnalytics = (params: {
  content: any;
  index: number;
}): any => {
  const { content, index } = params;

  const analytics = content.analytics ?
    {
      ...content,
      analytics: content.analytics[0]
    } :
    content;

  const data = new ReportingService().fixAnalyticsValues(
    analytics,
    null,
    ComponentNames.CHLUP_INTERACTIVE_TOOL,
    index
  );

  data.analytics.contentSystem = "STORYBLOK";
  return data.analytics;
};
// Generated by Copilot- End
